import React, { useState, useEffect } from 'react';
import { TableLoader } from '../../../components/Common/Loader';
import { Plus, Search, Edit2, X, Check, ArrowLeft, Eye } from 'lucide-react';
import { getZonesAPI, createZoneAPI, updateZoneAPI } from '../../../api/locations';
import CSVButton from '../../../components/Common/CSVButton';
import { exportToCSV } from '../../../utils/csvExport';

export interface ZoneItem {
  id: number;
  name: string;
  sapCode?: string | null;
  status: number | string;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

const initialZonesMock: ZoneItem[] = [
  { id: 2, name: 'SOUTH', status: 1 },
  { id: 1, name: 'WEST', status: 1 },
];

export const ZonesList: React.FC = () => {
  const [zones, setZones] = useState<ZoneItem[]>(initialZonesMock);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<ZoneItem | null>(null);
  const [viewingZone, setViewingZone] = useState<ZoneItem | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'Active' | 'Inactive'>('Active');

  const fetchZones = async () => {
    try {
      setLoading(true);
      const res = await getZonesAPI();
      const rawList = res?.data || res?.zones || (Array.isArray(res) ? res : null);
      if (Array.isArray(rawList)) {
        setZones(rawList);
      }
    } catch (err) {
      console.warn('Using fallback data for zones:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, []);

  const handleOpenAddModal = () => {
    setEditingZone(null);
    setName('');
    setStatus('Active');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (zone: ZoneItem) => {
    setEditingZone(zone);
    setName(zone.name);
    setStatus(zone.status === 1 || zone.status === 'Active' ? 'Active' : 'Inactive');
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      if (editingZone) {
        await updateZoneAPI(editingZone.id, {
          name,
          status: status === 'Active' ? 1 : 0,
        });
      } else {
        await createZoneAPI({
          name,
        });
      }
      await fetchZones();
      setIsModalOpen(false);
    } catch (err: any) {
      console.error('Error saving zone:', err);
      alert(err?.message || 'Failed to save zone');
    }
  };

  const filteredZones = zones.filter((z) =>
    z.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleExportCSV = () => {
    const headers = ['ID', 'Zone Name', 'Status'];
    const rows = filteredZones.map((z) => [
      z.id,
      z.name,
      z.status === 1 || z.status === '1' || z.status === 'Active' ? 'Active' : 'Inactive',
    ]);
    exportToCSV('zones_list', headers, rows);
  };

  return (
    <div className="flex flex-col gap-6 pb-6 select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Zones Management
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAddModal}
            className="px-4 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl shadow-md shadow-[#C1272D]/20 flex items-center gap-2 cursor-pointer transition-all active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>Add Zone</span>
          </button>
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col">
        {/* Controls Bar */}
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-base font-bold text-slate-800">All Zones</h3>

          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
            <input
              type="text"
              placeholder="Search zones..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 focus:outline-none focus:border-[#C1272D]"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                <th className="py-3.5 px-6">ID</th>
                <th className="py-3.5 px-6">Zone Name</th>
                <th className="py-3.5 px-6">Status</th>
                <th className="py-3.5 px-6 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
              {filteredZones.map((z) => {
                const isActive = z.status === 1 || z.status === 'Active';
                return (
                  <tr key={z.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-4 px-6 font-medium text-slate-400">{z.id}</td>
                    <td className="py-4 px-6 font-bold text-slate-800">{z.name}</td>
                    <td className="py-4 px-6 text-xs text-[#828282]">{isActive ? 'Active' : 'Inactive'}</td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setViewingZone(z)}
                          className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenEditModal(z)}
                          className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {loading ? (
                <TableLoader colSpan={4} text="Loading..." />
              ) : filteredZones.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-slate-400 font-medium">
                    No zones found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Zone Modal */}
      {viewingZone && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-extrabold text-slate-800">{viewingZone.name}</h3>
                <p className="text-xs font-mono font-medium text-slate-400">Zone ID: {viewingZone.id}</p>
              </div>
              <button
                onClick={() => setViewingZone(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4 text-xs">
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Zone Name</span>
                <p className="text-sm font-bold text-slate-900">{viewingZone.name}</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Status</span>
                <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${
                  (viewingZone.status === 1 || viewingZone.status === 'Active') ? 'bg-emerald-100/80 text-emerald-700' : 'bg-rose-100/80 text-rose-700'
                }`}>
                  {(viewingZone.status === 1 || viewingZone.status === 'Active') ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="flex justify-end pt-4 border-t border-slate-100">
              <button
                onClick={() => setViewingZone(null)}
                className="px-6 py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs rounded-xl cursor-pointer transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <h3 className="text-lg font-extrabold text-slate-800">
                {editingZone ? 'Edit Zone' : 'Add New Zone'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div className="space-y-1">
                <label className="font-bold text-slate-700">Zone Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. WEST"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 focus:outline-none focus:border-[#C1272D]"
                />
              </div>

              <div className="space-y-1 pt-1">
                <label className="font-bold text-slate-700 block">Status</label>
                <div className="flex items-center gap-6 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-slate-700">
                    <input
                      type="radio"
                      name="zoneStatus"
                      value="Active"
                      checked={status === 'Active'}
                      onChange={() => setStatus('Active')}
                      className="accent-[#C1272D]"
                    />
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-md text-[11px] font-bold">
                      Active
                    </span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-slate-700">
                    <input
                      type="radio"
                      name="zoneStatus"
                      value="Inactive"
                      checked={status === 'Inactive'}
                      onChange={() => setStatus('Inactive')}
                      className="accent-[#C1272D]"
                    />
                    <span className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded-md text-[11px] font-bold">
                      Inactive
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white font-bold rounded-xl flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>{editingZone ? 'Save Changes' : 'Add Zone'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZonesList;

